import React from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { normalizeQuantity } from '../utils/quantity';

const resolveQuantity = (item, variety, getItemQuantity) => {
  if (typeof getItemQuantity === 'function') {
    return getItemQuantity(item, variety);
  }
  return item?.quantity ?? 0;
};

const resolveDiscountAmount = ({ itemsSubtotal, orderDiscountPercentage, orderDiscountAmount }) => {
  const subtotal = Number(itemsSubtotal || 0);
  if (typeof orderDiscountPercentage === 'number') {
    return Math.floor(subtotal * (orderDiscountPercentage / 100));
  }
  return Number(orderDiscountAmount || 0);
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const normalizeLabel = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const buildItemDisplayName = (item, variety) => {
  const productName = normalizeLabel(item?.productName || variety?.productName || variety?.Product?.productName);
  const varietyName = normalizeLabel(item?.varietyName || variety?.varietyName);
  if (productName && varietyName) {
    if (productName.toLowerCase() === varietyName.toLowerCase()) {
      return productName;
    }
    return `${productName} - ${varietyName}`;
  }
  return productName || varietyName || 'Producto';
};

const CartPanel = ({
  items = [],
  getVariety,
  getItemQuantity,
  onItemClick,
  onRemoveItem,
  canEdit = true,
  headerContent,
  panelClassName = 'bg-white shadow-lg rounded-lg p-4',
  footerClassName = 'flex flex-col gap-1 mt-4 bottom-0',
  itemsSubtotal = 0,
  orderDiscountPercentage,
  orderDiscountAmount,
  totalAmount = 0,
  footerActions,
  pendingItemDiscounts = {},
  pendingOrderDiscount = null,
  onResumePendingItemDiscount,
  onResumePendingOrderDiscount,
  canOverridePriceType = false,
  getItemPriceTypeOptions,
  onItemPriceTypeChange,
  getItemPricing,
  showSummaryBreakdown = true,
  showPrices = true,
  showTotal = showPrices,
}) => {
  const effectiveOrderDiscount = resolveDiscountAmount({
    itemsSubtotal,
    orderDiscountPercentage,
    orderDiscountAmount,
  });

  const handleItemClick = (item, variety) => {
    if (!onItemClick) return;
    onItemClick(item, variety);
  };

  return (
    <>
      <div className={panelClassName}>
        {headerContent && <div className="sticky top-0 bg-white pb-3 z-10">{headerContent}</div>}
        {items.length > 0 &&
          items.map((item, index) => {
            const variety = getVariety ? getVariety(item) : null;
            const imageSrc =
              item?.priceImage ||
              item?.varietyImage ||
              item?.productImage ||
              variety?.VarietyPrices?.[item.priceIndex]?.priceImage ||
              variety?.varietyImage ||
              'https://placehold.co/50';
            const itemQuantity = normalizeQuantity(
              resolveQuantity(item, variety, getItemQuantity)
            );
            const itemPrice = item.price ?? 0;
            const itemDiscount = item.discount ?? 0;
            const discountedPrice = itemPrice - itemDiscount;
            const pricing = typeof getItemPricing === 'function'
              ? getItemPricing(item, variety, itemQuantity)
              : null;
            const displayItemPrice = Number.isFinite(Number(pricing?.unitPrice))
              ? Number(pricing.unitPrice)
              : itemPrice;
            const hasPromotion = item?.discountSource === 'promotion'
              && Number(item?.promotionDiscountAmount || 0) > 0;
            const displayDiscountedPrice = Number.isFinite(Number(pricing?.discountedUnitPrice))
              ? Number(pricing.discountedUnitPrice)
              : hasPromotion && itemQuantity > 0 && Number.isFinite(Number(item?.subtotal))
                ? Number(item.subtotal) / itemQuantity
                : discountedPrice;
            const displaySubtotal = Number.isFinite(Number(pricing?.subtotal))
              ? Number(pricing.subtotal)
              : Number.isFinite(Number(item?.subtotal))
                ? Number(item.subtotal)
                : (displayDiscountedPrice * itemQuantity);
            const hasDiscount = Math.abs(displayItemPrice - displayDiscountedPrice) > 0.0001;
            const taxFreeApplied = Boolean(pricing?.taxFreeApplied);
            const itemDescription =
              variety?.VarietyPrices?.[item.priceIndex]?.description
              || item?.description
              || item?.metricUnit
              || 'unidad';
            const itemDisplayName = buildItemDisplayName(item, variety);
            const pendingKey = `${item.varietyId}-${item.priceIndex}`;
            const pendingEntry = pendingItemDiscounts?.[pendingKey] || null;
            const priceTypeOptions = typeof getItemPriceTypeOptions === 'function'
              ? getItemPriceTypeOptions(item, variety)
              : [];
            const canEditPriceType = canOverridePriceType
              && typeof onItemPriceTypeChange === 'function'
              && Array.isArray(priceTypeOptions)
              && priceTypeOptions.length > 0;
            const priceTypeValue = item?.priceTypeOverride
              ? (item?.priceType || '')
              : '';

            return (
              <div
                key={`${item.varietyId}-${item.priceIndex}-${index}`}
                className="relative bg-white border rounded-md shadow-sm px-3 py-2 cursor-pointer hover:bg-gray-50 mb-2"
                onClick={() => handleItemClick(item, variety)}
              >
                {canEdit && onRemoveItem && (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveItem(item);
                    }}
                    className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1"
                  >
                    <FiX size={14} />
                  </button>
                )}
                <div className="flex items-center gap-3 pr-8">
                  <img
                    src={imageSrc}
                    alt={itemDisplayName}
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm leading-snug truncate">
                          {itemDisplayName}
                        </div>
                        <div className="text-xs text-gray-600 leading-snug mt-0.5 truncate">
                          {itemDescription}
                        </div>
                      </div>

                      <div className={`${showPrices ? 'w-[118px]' : ''} shrink-0 text-right`}>
                        <span className="inline-flex items-center justify-center rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                          Cant: {formatMoney(itemQuantity)}
                        </span>
                        {showPrices && (
                          <div className="mt-1">
                            <div className="truncate text-[11px] leading-tight text-gray-500">
                              {hasDiscount ? (
                                <>
                                  <span className="line-through mr-1">
                                    ${formatMoney(displayItemPrice)}
                                  </span>
                                  <span className="text-gray-700">
                                    ${formatMoney(displayDiscountedPrice)}
                                  </span>
                                  <span className="ml-1 text-red-600">
                                    ({displayItemPrice > 0 ? `-${Math.round(((displayItemPrice - displayDiscountedPrice) / displayItemPrice) * 100)}%` : '-0%'})
                                  </span>
                                </>
                              ) : (
                                `Precio: $${formatMoney(displayItemPrice)}`
                              )}
                              {taxFreeApplied && (
                                <span
                                  className="ml-1 inline-flex items-center text-blue-600 align-middle"
                                  title="Precio ajustado sin impuestos excluidos."
                                  aria-label="Precio ajustado sin impuestos excluidos."
                                >
                                  <FiInfo size={12} />
                                </span>
                              )}
                            </div>
                            <div className="text-sm font-semibold leading-tight text-gray-900">
                              ${formatMoney(displaySubtotal)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {canEditPriceType && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-600">Tipo precio</label>
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                      value={priceTypeValue}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        onItemPriceTypeChange(item, event.target.value || '');
                      }}
                    >
                      <option value="">Auto</option>
                      {priceTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {item?.priceTypeOverride && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Manual
                      </span>
                    )}
                  </div>
                )}
                {hasPromotion && (
                  <div className="mt-2 inline-flex max-w-full items-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 border border-emerald-100">
                    <span className="truncate">
                      Promo: {item.promotionName || 'Promocion'}
                    </span>
                  </div>
                )}
                {pendingEntry && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                    <span>Descuento pendiente</span>
                    {onResumePendingItemDiscount && (
                      <button
                        type="button"
                        className="text-amber-800 underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          onResumePendingItemDiscount(pendingEntry);
                        }}
                      >
                        Continuar
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      <div className={footerClassName}>
        {showSummaryBreakdown && (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium">Subtotal productos:</span>
              <span>${Number(itemsSubtotal || 0).toLocaleString('de-DE')}</span>
            </div>
            {effectiveOrderDiscount > 0 && (
              <div className="flex justify-between items-center text-sm text-red-600">
                <span className="font-medium">
                  Descuento orden
                  {orderDiscountPercentage != null ? ` (${orderDiscountPercentage}%)` : ''}:
                </span>
                <span>- ${effectiveOrderDiscount.toLocaleString('de-DE')}</span>
              </div>
            )}
            {pendingOrderDiscount && (
              <div className="flex items-center justify-between gap-2 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded px-2 py-1">
                <span>Descuento de orden pendiente</span>
                {onResumePendingOrderDiscount && (
                  <button
                    type="button"
                    className="text-amber-800 underline"
                    onClick={() => onResumePendingOrderDiscount(pendingOrderDiscount)}
                  >
                    Continuar
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {showTotal && (
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm font-bold">Total:</span>
            <span className="text-md font-bold text-blue-500">
              ${Number(totalAmount || 0).toLocaleString('de-DE')}
            </span>
          </div>
        )}
        {footerActions}
      </div>
    </>
  );
};

export default CartPanel;
